import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { FeatureFlagsService } from '../shared-service/feature-flags.service';
import * as amplitude from 'amplitude-js';
import { LFEventProperty } from './amplitude-analytics.model';
import { CommonUtil } from '../Utils/common-util';
import { FacilityService } from './facility.service';
import { ActivatedRoute } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class AmplitudeAnalyticsService {
  private initPromise: Promise<unknown> | undefined;
  private isLoggedIn = false;
  private lastSelectedFacilityId: string;
  private lastSelectedConsole: string;
  private interactionTimeout: any;
  private isPageInteraction = false;
  private lastInteraction: number;
  private currentPageName: string;
  private featureName: string;
  constructor(
    private _featureFlagService: FeatureFlagsService,
    private _commonUtil: CommonUtil,
    private _facilityService: FacilityService,
    private _route: ActivatedRoute
  ) { 
    this.init();
  }

  set pageName(pageName: string) {
    this.currentPageName = pageName;
    this.featureName = '';
  }

  async init() {
    try {      
      this.initPromise = this.initialize();
      await this.initPromise;
    } catch (error) {
      console.error('Error init amplitude');
      this.initPromise = undefined;
    }
  }

  async logEvent(eventProperties?: LFEventProperty, featureName?: string) {
    if(this.initPromise) await this.initPromise;
    const enableTracking = this._featureFlagService.isOff('user-activity-tracking-off') && this._featureFlagService.isOn('user-activity-tracking');
    if(this.isLoggedIn && !enableTracking) return;
    if(!this.currentPageName && !featureName) return;
    const userProp = [];
    if(this.lastSelectedFacilityId !== this._facilityService.selectedFacilityId()) {
      this.lastSelectedFacilityId = this._facilityService.selectedFacilityId();
      userProp.push({key: 'Facility ID', value: this.lastSelectedFacilityId});
    }
    // if(this.lastSelectedConsole !== this._commonUtil.rootScopeData.lastSelectedConsole) {
    //   this.lastSelectedConsole = this._commonUtil.rootScopeData.lastSelectedConsole;
    //   userProp.push({key: 'Console', value: this.lastSelectedConsole});
    // }
    if(userProp.length > 0) this.setUserProperty(userProp);
    if(enableTracking) this.monitorPageInteraction();
    if(this._route?.snapshot?.firstChild?.data?.meta?.subFeatureName){
      if(eventProperties?.additionalProperty) eventProperties.additionalProperty.subFeatureName = this._route.snapshot.firstChild.data.meta.subFeatureName;
      else if(eventProperties) eventProperties.additionalProperty = {subFeatureName: this._route.snapshot.firstChild.data.meta.subFeatureName}
    }
    if(featureName) this.featureName = featureName;
    amplitude.getInstance().logEvent(this.featureName||this.currentPageName, eventProperties);
  }

  async setUser(userId: string){
    if(this.initPromise) await this.initPromise;
    this.isLoggedIn = true;
    amplitude.getInstance().setUserId(userId);
  }
  
  async setUserProperty(prop: {key: string, value: string}[]) {
    if(this.initPromise) await this.initPromise;
    if(this.isLoggedIn && !this._featureFlagService.isOn('user-activity-tracking')) return;
    const identify = new amplitude.Identify();
    prop.forEach(v => identify.set(v.key, v.value));    
    amplitude.getInstance().identify(identify);
  }

  async reset() {
    if(this.initPromise) await this.initPromise;
    this.isLoggedIn = false;
    amplitude.getInstance().setUserId(null);
    amplitude.getInstance().regenerateDeviceId();
  }

  setUserInteraction() {
    this.lastInteraction = Date.now() - 1000; // make sure no double log
    this.isPageInteraction = true;
  }

  private monitorPageInteraction() {
    this.isPageInteraction = false; // reset
    if(this.interactionTimeout) clearTimeout(this.interactionTimeout);
    // monitor feature usage by checking user page interaction
    this.interactionTimeout = setTimeout(() => {
      if(this.isPageInteraction && Date.now() - this.lastInteraction < 60000) {
        this.logEvent({elementName: 'Page', action: 'User Interaction'});
      }
      this.isPageInteraction = false; // reset
    }, 60000); // 1 min
  }

  private initialize() {
    return new Promise((resolve) => {
      amplitude.getInstance().init(environment.amplitudeAPI, undefined, undefined, () => {
        resolve(true);
      });
    });
  }
}
